import type {
  BillingPeriod,
  PlanMode,
  Tier,
} from '../components/upgrade/upgrade-data';
import type { UpgradeContext } from '@tabbio/types';

export type UpgradeRouteOptions = {
  mode?: PlanMode;
  tier?: Tier;
  billing?: BillingPeriod;
  context?: UpgradeContext;
};

export type UpgradeOpenInput = PlanMode | UpgradeContext;

/** Optional presentation overrides for the paywall (e.g. preselect yearly). */
export type UpgradeOpenOverrides = {
  tier?: Tier;
  billing?: BillingPeriod;
};

export type UpgradeDialogProps = {
  mode?: PlanMode;
  context?: UpgradeContext;
  tier?: Tier;
  billing?: BillingPeriod;
};

export function normalizeUpgradeModeParam(value: unknown): PlanMode {
  return value === 'employer' ? 'employer' : 'individual';
}

export function normalizeUpgradeTierParam(value: unknown): Tier {
  return value === 'pro' ? 'pro' : 'plus';
}

export function normalizeUpgradeBillingParam(value: unknown): BillingPeriod {
  return value === 'yearly' ? 'yearly' : 'monthly';
}

export function buildUpgradeRoute({
  mode = 'individual',
  tier = 'plus',
  billing,
  context,
}: UpgradeRouteOptions = {}) {
  return {
    pathname: '/upgrade',
    params: {
      mode,
      tier,
      ...(billing ? { billing } : {}),
      ...(context
        ? {
            mode: context.planSet,
            tier: context.recommendedTier,
            placement: context.placement,
            entryPoint: context.entryPoint,
            actionKey: context.actionKey,
            featureKey: context.featureKey,
            reason: context.reason,
            allowanceRemaining:
              context.allowanceRemaining === undefined
                ? undefined
                : String(context.allowanceRemaining),
            allowanceLimit:
              context.allowanceLimit === undefined
                ? undefined
                : String(context.allowanceLimit),
            allowanceUnit: context.allowanceUnit,
            resetAt: context.resetAt,
            returnTo: context.returnTo,
            draftId: context.draftId,
            resumeToken: context.resumeToken,
          }
        : {}),
    },
  } as const;
}

/** Builds the canonical paywall route from the public modal API input. */
export function buildUpgradeRouteFromInput(
  input?: UpgradeOpenInput,
  overrides?: UpgradeOpenOverrides,
) {
  if (typeof input === 'string' || input === undefined) {
    return buildUpgradeRoute({
      mode: input,
      tier: overrides?.tier,
      billing: overrides?.billing,
    });
  }

  return buildUpgradeRoute({
    mode: input.planSet,
    tier: input.recommendedTier,
    billing: overrides?.billing,
    context: input,
  });
}

/**
 * Maps the public upgrade API input onto the web dialog props. Keep this next
 * to the native route mapper so web and native preserve the same contextual
 * entitlement payload instead of treating an UpgradeContext object as a mode.
 */
export function buildUpgradeDialogProps(
  input?: UpgradeOpenInput,
  overrides?: UpgradeOpenOverrides,
): UpgradeDialogProps | undefined {
  const base: UpgradeDialogProps = {
    ...(overrides?.tier ? { tier: overrides.tier } : {}),
    ...(overrides?.billing ? { billing: overrides.billing } : {}),
  };

  if (typeof input === 'string') {
    return { ...base, mode: input };
  }

  if (input) {
    return { ...base, mode: input.planSet, context: input };
  }

  return overrides ? base : undefined;
}

export function normalizeUpgradeContextParams(params: {
  placement?: string;
  entryPoint?: string;
  mode?: string;
  tier?: string;
  actionKey?: string;
  featureKey?: string;
  reason?: string;
  allowanceRemaining?: string;
  allowanceLimit?: string;
  allowanceUnit?: string;
  resetAt?: string;
  returnTo?: string;
  draftId?: string;
  resumeToken?: string;
}): UpgradeContext | undefined {
  if (!params.placement || !params.entryPoint) {
    return undefined;
  }

  const toNumber = (value?: string) => {
    const parsed = value === undefined ? Number.NaN : Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  };

  return {
    placement: params.placement as UpgradeContext['placement'],
    entryPoint: params.entryPoint,
    planSet: normalizeUpgradeModeParam(params.mode),
    recommendedTier: normalizeUpgradeTierParam(params.tier),
    actionKey: params.actionKey as UpgradeContext['actionKey'],
    featureKey: params.featureKey as UpgradeContext['featureKey'],
    reason: params.reason as UpgradeContext['reason'],
    allowanceRemaining: toNumber(params.allowanceRemaining),
    allowanceLimit: toNumber(params.allowanceLimit),
    allowanceUnit: params.allowanceUnit as UpgradeContext['allowanceUnit'],
    resetAt: params.resetAt,
    returnTo: params.returnTo,
    draftId: params.draftId,
    resumeToken: params.resumeToken,
  };
}
